import { supabase } from '../supabaseClient';
import { AnnualTimetableWeekMemo, AnnualTimetableWeekMemoUpsert } from '../types/domain';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import { canWriteSchoolScopedDataCache, notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import { deviceCacheStorage } from '../utils/deviceCacheStorage';

const SELECT =
  'id, workspace_id, grade_id, academic_year, term_number, monday_iso, activity_lines, general_memo, created_at, updated_at';

const WEEK_MEMOS_CACHE_VERSION = 'v1';

function weekMemosCacheKey(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): string {
  const sorted = Array.from(new Set(gradeIds.filter(Boolean))).sort().join(',');
  return `cp.annualTimetableWeekMemos.${WEEK_MEMOS_CACHE_VERSION}.${workspaceId}.${academicYear}.${termNumber}.${sorted}`;
}

export function loadAnnualTimetableWeekMemosLocalCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): { found: boolean; rows: AnnualTimetableWeekMemo[] } {
  if (!workspaceId || gradeIds.length === 0 || typeof window === 'undefined') {
    return { found: false, rows: [] };
  }
  const uniqueGradeIds = Array.from(new Set(gradeIds.filter(Boolean)));
  const cacheKey = weekMemosCacheKey(workspaceId, academicYear, termNumber, uniqueGradeIds);
  try {
    const raw = deviceCacheStorage.getItem(cacheKey);
    if (raw != null && raw !== '') {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        return { found: true, rows: parsed.map((row) => mapRow(row as Record<string, unknown>)) };
      }
    }
  } catch {
    /* ignore */
  }
  return { found: false, rows: [] };
}

function clearWeekMemosCachesForScope(workspaceId: string, academicYear: number, termNumber: 1 | 2): void {
  if (!workspaceId || typeof window === 'undefined') return;
  const prefix = `cp.annualTimetableWeekMemos.${WEEK_MEMOS_CACHE_VERSION}.${workspaceId}.${academicYear}.${termNumber}.`;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const k = deviceCacheStorage.key(i);
      if (k && k.startsWith(prefix)) toRemove.push(k);
    }
    toRemove.forEach((key) => deviceCacheStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

const pendingWeekMemos = new Map<string, Promise<AnnualTimetableWeekMemo[]>>();

function mapRow(row: Record<string, unknown>): AnnualTimetableWeekMemo {
  const rawLines = row.activity_lines;
  const activity_lines = Array.isArray(rawLines)
    ? rawLines.map((x) => (typeof x === 'string' ? x : String(x ?? '')))
    : [];
  return {
    id: String(row.id ?? ''),
    workspace_id: String(row.workspace_id ?? ''),
    grade_id: String(row.grade_id ?? ''),
    academic_year: Number(row.academic_year ?? 0),
    term_number: row.term_number === 2 ? 2 : 1,
    monday_iso: String(row.monday_iso ?? ''),
    activity_lines,
    general_memo: String(row.general_memo ?? ''),
    created_at: row.created_at != null ? String(row.created_at) : undefined,
    updated_at: row.updated_at != null ? String(row.updated_at) : undefined,
  };
}

export async function getAnnualTimetableWeekMemos(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): Promise<AnnualTimetableWeekMemo[]> {
  if (!workspaceId || gradeIds.length === 0) return [];
  const uniqueGradeIds = Array.from(new Set(gradeIds.filter(Boolean)));
  const cacheKey = weekMemosCacheKey(workspaceId, academicYear, termNumber, uniqueGradeIds);

  if (typeof window !== 'undefined') {
    try {
      const raw = deviceCacheStorage.getItem(cacheKey);
      if (raw != null && raw !== '') {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
          if (process.env.NODE_ENV === 'development') {
            recordDevLocalStorageProgramCacheUse('연간주메모·LS');
          }
          return parsed.map((row) => mapRow(row as Record<string, unknown>));
        }
      }
    } catch {
      /* ignore */
    }
  }

  const inflight = pendingWeekMemos.get(cacheKey);
  if (inflight) return inflight;

  const task = (async () => {
    try {
      const { data, error } = await supabase
        .from('annual_timetable_week_memos')
        .select(SELECT)
        .eq('workspace_id', workspaceId)
        .eq('academic_year', academicYear)
        .eq('term_number', termNumber)
        .in('grade_id', uniqueGradeIds);
      if (error) {
        throw new Error(error.message);
      }
      const rows = (data ?? []).map((row) => mapRow(row as Record<string, unknown>));
      if (typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
        try {
          deviceCacheStorage.setItem(cacheKey, JSON.stringify(data ?? []));
        } catch {
          /* ignore quota */
        }
      }
      return rows;
    } finally {
      pendingWeekMemos.delete(cacheKey);
    }
  })();

  pendingWeekMemos.set(cacheKey, task);
  return task;
}

export async function upsertAnnualTimetableWeekMemo(
  row: AnnualTimetableWeekMemoUpsert
): Promise<AnnualTimetableWeekMemo> {
  const payload = {
    ...row,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase
    .from('annual_timetable_week_memos')
    .upsert(payload, {
      onConflict: 'workspace_id,grade_id,academic_year,term_number,monday_iso',
    })
    .select(SELECT)
    .limit(1);
  if (error) {
    throw new Error(error.message);
  }
  const first = (data ?? [])[0];
  if (!first) {
    throw new Error('주 메모 저장 후 응답이 비어 있습니다.');
  }
  const mapped = mapRow(first as Record<string, unknown>);
  clearWeekMemosCachesForScope(mapped.workspace_id, mapped.academic_year, mapped.term_number);
  await notifySchoolProgramMutation(mapped.workspace_id, ['annualTimetable']);
  return mapped;
}
